import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import { useQueryClient } from '@tanstack/react-query';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Spin from 'antd/es/spin';
import { type ColumnType } from 'antd/es/table';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { type Task, type TasksQueryOptions } from '@mai/types';

import ContentContainer from '../ContentContainer';
import Table from '../Table';
import TableHeader from '../TableHeader';
import {
  usePageFilter,
  usePageSizeFilter,
  useSortFilter,
  useTaskTitleFilter,
} from './TasksTable.hooks';

import { TASKS_QUERY_KEY, useTasksQuery } from '@queries/tasks';
import { renderDate } from '@utils/dates';
import { formatNumber } from '@utils/numbers';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TasksTableHeader = ({
  dataSource,
  pageSize,
  isLoading: _isLoading,
  isFetching,
  size,
  totalLength,
}: {
  dataSource: Task[];
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  size: 'small' | 'middle' | 'large';
  totalLength?: number;
}) => {
  const queryClient = useQueryClient();
  const { taskTitleFilterValue, setTaskTitleFilter } = useTaskTitleFilter();
  const { pageFilterValue, setPageFilter } = usePageFilter();
  const { setPageSizeFilter } = usePageSizeFilter();

  // Local state for search input
  const [searchValue, setSearchValue] = useState(taskTitleFilterValue ?? '');

  // Debounce search input to prevent excessive URL updates
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  // Sync debounced search value to URL params
  useEffect(() => {
    if (debouncedSearchValue === taskTitleFilterValue) {
      return;
    }
    setTaskTitleFilter(debouncedSearchValue);
  }, [debouncedSearchValue, taskTitleFilterValue, setTaskTitleFilter]);

  // If URL parameter changes from elsewhere, update the local input value
  useEffect(() => {
    if (
      taskTitleFilterValue !== undefined &&
      taskTitleFilterValue !== null &&
      taskTitleFilterValue !== searchValue
    ) {
      setSearchValue(taskTitleFilterValue);
    }
  }, [taskTitleFilterValue, searchValue]);

  return (
    <TableHeader>
      <HeaderContainer>
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
          <Input
            placeholder="Search for tasks by title..."
            allowClear
            size={size}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <ControlsContainer>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '0.25rem',
              marginLeft: '0.75rem',
            }}
          >
            {totalLength !== undefined ? (
              <Tag
                style={{
                  fontSize: size === 'small' ? '0.75rem' : '0.8rem',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                }}
              >
                {formatNumber(totalLength, true)} Task
                {totalLength === 1 ? '' : 's'}
              </Tag>
            ) : (
              <Spin />
            )}
          </div>
          <Select
            options={[
              { label: '10', value: '10' },
              { label: '25', value: '25' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
            size={size}
            value={pageSize}
            suffixIcon={<>/ Page</>}
            onChange={(value) => {
              setPageSizeFilter(value);
            }}
            style={{ width: '5rem' }}
          />
          <Button
            size={size}
            disabled={pageFilterValue <= 1}
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              setPageFilter(pageFilterValue - 1);
            }}
          />
          <Button
            size={size}
            disabled={dataSource.length < pageSize}
            icon={<ArrowRightOutlined />}
            onClick={() => {
              setPageFilter(pageFilterValue + 1);
            }}
          />
          <Button
            size={size}
            disabled={isFetching}
            icon={<SyncOutlined spin={isFetching} />}
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [TASKS_QUERY_KEY],
              });
            }}
          />
        </ControlsContainer>
      </HeaderContainer>
    </TableHeader>
  );
};

const TasksTable = ({
  size = 'middle',
  showHeader = true,
  projectId,
}: {
  size?: 'small' | 'middle' | 'large';
  showHeader?: boolean;
  projectId?: string;
}) => {
  const { teamId } = useParams<{ teamId: string }>();

  // Filters
  const { taskTitleFilterValue } = useTaskTitleFilter();
  const { pageSizeFilterValue } = usePageSizeFilter();
  const { pageFilterValue } = usePageFilter();
  const { sortByFilterValue, sortDirectionFilterValue } = useSortFilter();

  const filters: TasksQueryOptions = {
    // Data Filters
    projectIds: projectId ? [projectId] : undefined,
    titleLike: taskTitleFilterValue || undefined,

    // Pagination
    pageSize: pageSizeFilterValue,
    orderBy: sortByFilterValue,
    orderDirection: sortDirectionFilterValue,
    page: pageFilterValue,
  };

  // Queries
  const tasksQuery = useTasksQuery(filters, true);
  const dataSource = tasksQuery.data ?? [];

  function renderStatus(status: Task['status']): React.ReactNode {
    switch (status) {
      case 'pending':
        return <Tag color="blue">Pending</Tag>;
      case 'in_progress':
        return <Tag color="green">In Progress</Tag>;
      case 'completed':
        return <Tag color="cyan">Completed</Tag>;
      case 'draft':
        return <Tag color="default">Draft</Tag>;
      case 'queued':
        return <Tag color="orange">Queued</Tag>;
    }
  }

  const columns: ColumnType<Task>[] = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Typography.Text>Title</Typography.Text>
          <Tooltip title="Click on the title to view task details">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: 'title',
      key: 'title',
      fixed: 'left',
      align: 'left',
      render: (value, record) => {
        const to = `/team/${teamId}/project/${projectId}/tasks/${record.taskId}`;
        return (
          <Link to={to}>
            <Typography.Text>{value || '-'}</Typography.Text>
          </Link>
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Typography.Text>Status</Typography.Text>
          <Tooltip title="Current task status">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      width: 130,
      render: (value) => renderStatus(value),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Typography.Text>Created At</Typography.Text>
          <Tooltip title="When the task was created">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (value) => renderDate(value),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Typography.Text>Updated At</Typography.Text>
          <Tooltip title="When the task was last updated">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (value) => renderDate(value),
    },
  ];

  if (tasksQuery.isError) {
    return <ContentContainer.Error />;
  }

  return (
    <>
      {showHeader && (
        <TasksTableHeader
          dataSource={dataSource}
          pageSize={pageSizeFilterValue}
          isLoading={tasksQuery.isLoading}
          isFetching={tasksQuery.isFetching}
          size={size}
          totalLength={dataSource.length}
        />
      )}
      <div style={{ display: 'flex', overflow: 'hidden' }}>
        <Table<Task>
          columns={columns}
          loading={tasksQuery.isLoading}
          dataSource={dataSource}
          size={size}
          rowKey="taskId"
          pagination={false}
          scroll={{ x: 700 }}
        />
      </div>
    </>
  );
};

export default TasksTable;
